import { addMinutes, differenceInSeconds } from "date-fns";
import { AppContext, BookingData } from "../../../AppDataProvider/AppDataProvider";
import { marginBottom, marginTop } from "../../styles";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ActionType } from "../../../AppDataProvider/actions";
import { applyOpacity } from "../../styles/utils";
import { faHourglassHalf } from "@fortawesome/free-solid-svg-icons";
import { Mixpanel } from "../../../mixpanel";
import Notification from "../Notification/Notification";
import { PartRequired } from "../../../utils/types";
import { requireBookingDataFields } from "../../../utils";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
export function ReservationExpiryTimer() {
  const {
    t
  } = useTranslation();
  const {
    appData,
    appDataDispatch
  } = useContext(AppContext);
  type ReservationExpiryTimerBookingData = PartRequired<BookingData, "organisation">;
  requireBookingDataFields<ReservationExpiryTimerBookingData>(appData.bookingData, ["organisation"]);
  const {
    bookingData
  } = appData;
  const {
    organisation
  } = bookingData;
  const {
    reservationExpiryMinutes
  } = organisation;

  /**
   * Calculate expiry time once and memoise so it doesn't get recalculated
   * on rerenders.
   */
  const expiresAt = useMemo(() => addMinutes(new Date(), reservationExpiryMinutes), [reservationExpiryMinutes]);
  const getRemainingSeconds = useCallback(() => {
    return differenceInSeconds(expiresAt, new Date());
  }, [expiresAt]);
  const [remainingSeconds, setRemainingSeconds] = useState<number>(getRemainingSeconds());
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (remainingSeconds > 0) {
        setRemainingSeconds(getRemainingSeconds());
      } else {
        Mixpanel.track({
          name: "Reservation expired"
        });
        appDataDispatch({
          payload: {
            buttonLabel: t("tryAgain"),
            content: t("sessionHasExpired", {
              count: reservationExpiryMinutes
            }),
            icon: faHourglassHalf,
            onClick: () => appDataDispatch({
              type: ActionType.RESET_AFTER_RESERVATION_EXPIRY
            }),
            title: t("sessionTimeout")
          },
          type: ActionType.SHOW_MODAL_DIALOG
        });
      }
    }, 1000);
    return () => clearInterval(timeout);
  }, [appDataDispatch, getRemainingSeconds, remainingSeconds, reservationExpiryMinutes, t]);
  const minutes = Math.floor(remainingSeconds / 60);
  const timeRemainingText = minutes >= 1 ? t("reservationTimeRemainingMinutes", {
    count: minutes
  }) : t("reservationTimeRemainingSeconds", {
    count: remainingSeconds
  });
  const notificationVariant = minutes ? "timeInfo" : "timeWarning";
  return <TimerNotification variant={notificationVariant} data-sentry-element="TimerNotification" data-sentry-component="ReservationExpiryTimer" data-sentry-source-file="ReservationExpiryTimer.tsx">
      {timeRemainingText}
    </TimerNotification>;
}
const TimerNotification = styled(Notification)`
  ${marginTop.m}
  ${marginBottom.m}

  /*
   * Notification.tsx (owned by a separate fixer lane — not touched here)
   * colours its "timeInfo"/"timeWarning" variants from the static,
   * non-brand-aware nonCustomisableColours.info/error palette, so it
   * always renders as a plain light-blue/red box regardless of the
   * booking tool's brand theme. Overriding background/colour here (after
   * Notification's own styles in source order, so it wins) swaps those
   * for the theme-aware --d-info/--d-error tokens instead, keeping the
   * same "informational tint" visual treatment (a low-opacity wash of the
   * semantic colour) but one that's correct for both light and dark
   * brand themes.
   */
  background: ${({
  theme,
  variant
}) => applyOpacity(variant === "timeWarning" ? theme.brand.cssVars["--d-error"] : theme.brand.cssVars["--d-info"], 0.05, true, theme.brand.cssVars["--d-surface"])};
  color: ${({
  theme,
  variant
}) => variant === "timeWarning" ? theme.brand.cssVars["--d-error"] : theme.brand.cssVars["--d-info"]};
`;
export default ReservationExpiryTimer;